export interface PayableTimeInterval {
  startMs: number;
  endMs: number;
  logId?: string;
}

export interface IdlePeriod {
  idle_start: string;
  idle_end: string | null;
  user_id?: string | null;
}

interface IdleFallbackTimeLog {
  id?: string;
  user_id: string;
  start_time: string;
  end_time: string | null;
  idle_seconds?: number | null;
}

export interface PayableTimeSummary {
  trackedSeconds: number;
  idleSeconds: number;
  payableSeconds: number;
}

interface PayableTimeOptions {
  rangeStartMs?: number;
  rangeEndMs?: number;
  nowMs?: number;
  fallbackIdleSeconds?: number;
  fallbackTimeLogs?: IdleFallbackTimeLog[];
}

/** Return the last sorted timestamp inside an inclusive interval in O(log n). */
export function findLastTimestampInRange(
  sortedTimestamps: number[],
  startMs: number,
  endMs: number
): number | undefined {
  let low = 0;
  let high = sortedTimestamps.length - 1;
  let match: number | undefined;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const timestamp = sortedTimestamps[middle];
    if (timestamp <= endMs) {
      if (timestamp >= startMs) match = timestamp;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return match;
}

function mergeIntervals(intervals: PayableTimeInterval[]): PayableTimeInterval[] {
  const valid = intervals
    .filter(interval => Number.isFinite(interval.startMs) && Number.isFinite(interval.endMs) && interval.endMs > interval.startMs)
    .sort((a, b) => a.startMs - b.startMs);

  if (valid.length === 0) return [];

  const merged: PayableTimeInterval[] = [{ ...valid[0] }];
  for (let index = 1; index < valid.length; index++) {
    const current = valid[index];
    const previous = merged[merged.length - 1];
    if (current.startMs <= previous.endMs) {
      previous.endMs = Math.max(previous.endMs, current.endMs);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
}

function getOverlapMs(first: PayableTimeInterval[], second: PayableTimeInterval[]): number {
  let firstIndex = 0;
  let secondIndex = 0;
  let overlapMs = 0;

  while (firstIndex < first.length && secondIndex < second.length) {
    const firstInterval = first[firstIndex];
    const secondInterval = second[secondIndex];
    const overlapStart = Math.max(firstInterval.startMs, secondInterval.startMs);
    const overlapEnd = Math.min(firstInterval.endMs, secondInterval.endMs);

    if (overlapEnd > overlapStart) overlapMs += overlapEnd - overlapStart;

    if (firstInterval.endMs <= secondInterval.endMs) {
      firstIndex++;
    } else {
      secondIndex++;
    }
  }

  return overlapMs;
}

function hasOverlap(intervals: PayableTimeInterval[], startMs: number, endMs: number): boolean {
  let low = 0;
  let high = intervals.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (intervals[middle].endMs <= startMs) low = middle + 1;
    else high = middle;
  }
  return low < intervals.length && intervals[low].startMs < endMs;
}

/**
 * Calculate the payroll-facing total from the same tracked intervals shown in reports.
 * Idle periods are merged and only their overlap with tracked time is deducted, so
 * duplicate idle rows and idle time outside a work session cannot reduce pay twice.
 */
export function calculatePayableTimeSummary(
  trackedIntervals: PayableTimeInterval[],
  idlePeriods: IdlePeriod[],
  options: PayableTimeOptions = {}
): PayableTimeSummary {
  const mergedTracked = mergeIntervals(trackedIntervals);
  const trackedMs = mergedTracked.reduce((sum, interval) => sum + (interval.endMs - interval.startMs), 0);
  const nowMs = options.nowMs ?? Date.now();

  const rawIdleIntervals = idlePeriods.flatMap(period => {
    const rawStartMs = new Date(period.idle_start).getTime();
    const rawEndMs = period.idle_end ? new Date(period.idle_end).getTime() : nowMs;
    if (!Number.isFinite(rawStartMs) || !Number.isFinite(rawEndMs) || rawEndMs <= rawStartMs) return [];
    return [{ startMs: rawStartMs, endMs: rawEndMs, userId: period.user_id || '' }];
  });
  const idleIntervals = rawIdleIntervals.flatMap(period => {
    const rawStartMs = period.startMs;
    const rawEndMs = period.endMs;

    const startMs = options.rangeStartMs != null ? Math.max(rawStartMs, options.rangeStartMs) : rawStartMs;
    const endMs = options.rangeEndMs != null ? Math.min(rawEndMs, options.rangeEndMs) : rawEndMs;
    return endMs > startMs ? [{ startMs, endMs }] : [];
  });

  // Canonical idle timestamps take precedence per session, rather than one
  // unrelated idle row disabling every session's stored counter. Old counters
  // have no writer provenance: some mean total idle and others final idle.
  // Infer a terminal interval only when that session has no canonical idle
  // evidence; never add a counter on top of existing evidence for the session.
  // Keep the raw session boundary before report/cap clipping so a missing
  // historical interval cannot be moved into earlier worked time.
  const reportableIds = new Set(trackedIntervals.map(interval => interval.logId).filter(Boolean));
  const canonicalByOwner = new Map<string, PayableTimeInterval[]>();
  for (const interval of rawIdleIntervals) {
    const owned = canonicalByOwner.get(interval.userId) || [];
    owned.push(interval);
    canonicalByOwner.set(interval.userId, owned);
  }
  for (const [owner, intervals] of canonicalByOwner) canonicalByOwner.set(owner, mergeIntervals(intervals));
  const fallbackIntervals = (options.fallbackTimeLogs || []).flatMap(log => {
    if (!log.id || !reportableIds.has(log.id)) return [];
    const startMs = Date.parse(log.start_time);
    const endMs = log.end_time ? Date.parse(log.end_time) : nowMs;
    const idleSeconds = Number(log.idle_seconds);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs
      || !Number.isFinite(idleSeconds) || idleSeconds <= 0) return [];
    const hasCanonicalIdle = hasOverlap(canonicalByOwner.get(log.user_id) || [], startMs, endMs)
      || hasOverlap(canonicalByOwner.get('') || [], startMs, endMs);
    const fallbackStartMs = Math.max(startMs, endMs - idleSeconds * 1000, options.rangeStartMs ?? -Infinity);
    const fallbackEndMs = Math.min(endMs, options.rangeEndMs ?? Infinity);
    return hasCanonicalIdle || fallbackEndMs <= fallbackStartMs
      ? [] : [{ startMs: fallbackStartMs, endMs: fallbackEndMs }];
  });

  let idleMs: number;
  if (options.fallbackTimeLogs || idleIntervals.length > 0) {
    idleMs = getOverlapMs(mergedTracked, mergeIntervals([...idleIntervals, ...fallbackIntervals]));
  } else {
    idleMs = Math.min(trackedMs, Math.max(0, options.fallbackIdleSeconds || 0) * 1000);
  }

  const trackedSeconds = Math.floor(trackedMs / 1000);
  const idleSeconds = Math.min(trackedSeconds, Math.floor(idleMs / 1000));

  return {
    trackedSeconds,
    idleSeconds,
    payableSeconds: Math.max(0, trackedSeconds - idleSeconds),
  };
}
